import { StepDataSecondGuarantee } from "@interfaces/SecondGuarantee";
import { RootState } from "@store/store";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

interface UseNextStepAndPatchResult {
  currentStep: StepDataSecondGuarantee | null;
  nextStep: StepDataSecondGuarantee | null;
  shouldPatch: boolean;
  nextStatus: {
    code: string;
    url: string;
    sequence: number;
    status_id: string;
  } | null;
}

export function useNextStepAndPatch(steps: StepDataSecondGuarantee[]): UseNextStepAndPatchResult {
  const location = useLocation();
  const currentPath = location.pathname;

  const [currentStep, setCurrentStep] = useState<StepDataSecondGuarantee | null>(null);

  const statusCatalog = useSelector((state: RootState) => state.secondGuarante.guarantee.status_config);
  const curretsStep = useSelector((state: RootState) => state.secondGuarante.guarantee.curretsStep);

  // Detectar paso actual basado en la URL
  useEffect(() => {
    const matched = steps.find((s) => s.url === currentPath) ?? null;
    setCurrentStep(matched);
  }, [currentPath, steps]);

  // Determinar si se debe hacer patch
  const shouldPatch = useMemo(() => {
    if (!currentStep || !curretsStep) return false;
    return currentStep.sequence > curretsStep.sequence;
  }, [currentStep, curretsStep]);

  // Calcular siguiente paso
  const nextStep = useMemo(() => {
    if (!currentStep) return null;
    const sortedSteps = [...steps].sort((a, b) => a.sequence - b.sequence);
    const currentIndex = sortedSteps.findIndex((s) => s.id === currentStep.id);
    return sortedSteps[currentIndex + 1] ?? null;
  }, [currentStep, steps]);

  // Obtener status completo del siguiente paso
  const nextStatus = useMemo(() => {
    if (!nextStep) return null;
    const match = statusCatalog.find((s) => s.url === nextStep.url);
    if (!match) return null;

    return {
      code: match.status_catalog?.code || "",
      url: match.url || "",
      sequence: nextStep.sequence,
      status_id: match.status_id || "",
    };
  }, [nextStep, statusCatalog]);

  return {
    currentStep,
    nextStep,
    shouldPatch,
    nextStatus,
  };
}
